import { LocalizedText } from "@/i18n/LocalizedText";
import { useState, useEffect, useMemo, useRef } from "react";
import { AppSidebar, data } from "@/components/AppSidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Tags,
  MapPin,
  Bot,
  FileText,
  Bell,
  BadgeCheck,
  ScanEye,
  Zap,
  CircleAlert,
  CalendarClock,
  Plus,
  Share2,
} from "lucide-react";
import trialArrow from "./assets/trial-arrow.svg";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import background from "./assets/first-screen.svg";
import { useAuth } from "./components/AuthContext";
import {
  authAPI,
  billingAPI,
  configAPI,
  dataChatAPI,
  type Plan,
} from "./services/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "./components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Outlet, useSearchParams } from "react-router";
import { Icon } from "@iconify/react";
import { countries } from "./countries";
import { useLocation } from "react-router-dom";
import { useSocketStore } from "@/store/useSocketStore";
import citationBlur from "./assets/citationsBlur.svg";
import dashboardBlur from "./assets/dashboardblur.svg";
import promptsBlur from "./assets/promptsBlur.png";
import strategyBlur from "./assets/strategyBlur.svg";
import competitorsBlur from "./assets/competitorsBlur.png";
import foundationBlur from "./assets/foundationBlur.png";
import aiModeIcon from "./assets/ai-mode.svg";
import graduationCapIcon from "./assets/ai-chat/graduation-cap.svg";
import { useNavigate } from "react-router-dom";
import type { JSX } from "react/jsx-runtime";
import grok from "./assets/grok.svg";
import copilot from "./assets/copilot.svg";
import { useQuery } from "@tanstack/react-query";
import { getUserPlan } from "./components/PlanSwitcher";
import { remoteConfig } from "./firebase";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { ClusterFilters } from "./components/ClusterFilters";
import { AnimatedNoboriLogoScanner } from "./components/AnimatedNoboriLogoScanner";
import { mapPlatformsToProviders } from "./utils/providers";
import { useDashboardFiltersStore } from "./store/useDashboardFiltersStore";
import { OnboardingHighlightManager } from "./components/OnboardingHighlightManager";
import {
  DOMAIN_TYPE_OPTIONS,
  URL_TYPE_OPTIONS,
  getDomainTypeBadgeClassName,
  getUrlTypeBadgeClassName,
} from "@/lib/urlTypes";
import { SourceTypeMultiSelect } from "@/components/ui/source-type-multi-select";
import { useLocale } from "@/i18n/LocaleProvider";
import { isWorkspaceMemberTier } from "@/lib/memberTier";
import { capturePosthogEvent } from "@/lib/posthog";
import {
  DateRangeFilter,
  getDateRangeForPreset,
} from "@/components/DateRangeFilter";
import {
  getBrandCompanyId,
  getWorkspaceId,
  resolveWorkspaceSelection,
} from "@/lib/workspaceSelection";

type OnboardingVideoConfig = {
  enabled: boolean;
  url: string;
};

const ALL_TYPES_FILTER_VALUE = "all";
const breadcrumbLabelKeys: Record<string, string> = {
  Dashboard: "shell.nav.dashboard",
  Prompts: "shell.nav.prompts",
  Competitors: "shell.nav.competitors",
  Sources: "shell.nav.sources",
  Foundation: "shell.nav.foundation",
  "Cluster Actions": "shell.nav.clusterActions",
  Strategy: "shell.nav.strategy",
  Tasks: "shell.nav.tasks",
  "Plans & Pricing": "shell.nav.plansPricing",
  Settings: "shell.nav.settings",
  Support: "shell.nav.support",
};
const BILLING_PAYMENT_ISSUE_STATUSES = new Set([
  "past_due",
  "unpaid",
  "incomplete",
  "incomplete_expired",
]);
const SHELL_SECONDARY_QUERY_DELAY_MS = 1200;

const hasDateEnded = (value: string | null | undefined) => {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return Date.now() > parsed.getTime();
};

const normalizeSourceTypeFilterValues = (values: string[]): string[] => {
  const normalized = Array.from(
    new Set(
      (values || []).map((value) => String(value || "").trim()).filter(Boolean),
    ),
  );
  const specific = normalized.filter(
    (value) => value !== ALL_TYPES_FILTER_VALUE,
  );
  if (specific.length) return specific;
  return [ALL_TYPES_FILTER_VALUE];
};

const normalizePlanToken = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isGrowthPlanOrHigher = (
  userTier: string | null | undefined,
  plan?: Plan | string,
) => {
  const planObject =
    typeof plan === "string" || !plan
      ? null
      : (plan as unknown as Record<string, unknown>);
  const tier = normalizePlanToken(userTier);
  const planId =
    typeof plan === "string" || !plan ? "" : normalizePlanToken(plan.id);
  const planName =
    typeof plan === "string" || !plan
      ? ""
      : normalizePlanToken(
          plan.name ??
            plan.metadata?.label ??
            planObject?.["slug"] ??
            planObject?.["key"],
        );

  const isStarterOrTrial = (value: string) => {
    if (!value) return false;
    if (value === "trial" || value === "starter" || value === "1") return true;
    return /\b(trial|starter)\b/.test(value);
  };

  const isGrowthOrHigherToken = (value: string) => {
    if (!value) return false;
    if (isStarterOrTrial(value)) return false;
    if (
      [
        "growth",
        "pro",
        "professional",
        "business",
        "enterprise",
        "scale",
      ].includes(value)
    ) {
      return true;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric >= 2;
    }
    return /\b(growth|pro(fessional)?|business|enterprise|scale)\b/.test(value);
  };

  if (isGrowthOrHigherToken(tier)) return true;
  if (isGrowthOrHigherToken(planId)) return true;
  if (!planName) return false;
  if (isStarterOrTrial(planName)) return false;
  return /\b(growth|pro(fessional)?|business|enterprise|scale)\b/.test(
    planName,
  );
};

const getEmbeddableVideoUrl = (rawUrl: string | undefined | null) => {
  if (!rawUrl) return "";
  try {
    const url = new URL(rawUrl);
    const hostname = url.hostname.toLowerCase();

    if (hostname.includes("youtube.com")) {
      const videoId = url.searchParams.get("v");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (url.pathname.startsWith("/embed/")) {
        return rawUrl;
      }
      const match = url.pathname.match(/\/shorts\/([^/]+)/);
      if (match?.[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    if (hostname === "youtu.be") {
      const videoId = url.pathname.replace("/", "");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    if (hostname.includes("loom.com")) {
      const id = url.pathname.split("/").pop();
      if (id) {
        return `https://www.loom.com/embed/${id}`;
      }
    }

    if (hostname.includes("vimeo.com")) {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id) {
        return `https://player.vimeo.com/video/${id}`;
      }
    }

    return rawUrl;
  } catch {
    return rawUrl || "";
  }
};

export const optionsPlatforms = [
  {
    label: "ChatGPT",
    value: "chatgpt",
    icon: <Icon icon="hugeicons:chat-gpt" width="24" height="24" />,
  },
  {
    label: "AI Overviews",
    value: "ai-overviews",
    icon: <Icon icon="flat-color-icons:google" width="24" height="24" />,
  },
  {
    label: "Perplexity",
    value: "perplexity",
    icon: <Icon icon="logos:perplexity-icon" width="24" height="24" />,
  },
  {
    label: "AI Mode",
    value: "ai-mode",
    icon: (
      <img src={aiModeIcon} className="w-4 h-4 object-contain" alt="AI Mode" />
    ),
  },
  {
    label: "Gemini",
    value: "gemini",
    soon: false,
    icon: <Icon icon="material-icon-theme:gemini-ai" width="24" height="24" />,
  },
  {
    label: "Grok",
    value: "grok",
    soon: false,
    icon: <img src={grok}></img>,
  },
  {
    label: "Copilot",
    value: "copilot",
    soon: false,
    icon: (
      <img src={copilot} className="w-4 h-4 object-contain" alt="Copilot" />
    ),
  },
];

export default function Dashboard() {
  const { t } = useLocale();
  const [breadcrumb, setBreadcrumb] = useState("Dashboard");
  const [clusterFilters, setClusterFilters] = useState({
    type: null,
    priority: null,
    effort: null,
  });
  const [timeRange, setTimeRange] = useState("7d");
  const [dateRange, setDateRange] = useState(() => getDateRangeForPreset("7d"));
  const handleSetTimeRange = (value: string) => {
    setTimeRange(value);
    if (["1d", "7d", "14d", "30d"].includes(value)) {
      setDateRange(getDateRangeForPreset(value));
    }
  };
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [citationsUrlTypeFilter, setCitationsUrlTypeFilter] = useState<
    string[]
  >([ALL_TYPES_FILTER_VALUE]);
  const [citationsDomainTypeFilter, setCitationsDomainTypeFilter] = useState<
    string[]
  >([ALL_TYPES_FILTER_VALUE]);
  const selectedPlatforms = useDashboardFiltersStore(
    (state) => state.selectedPlatforms,
  );
  const setSelectedPlatforms = useDashboardFiltersStore(
    (state) => state.setSelectedPlatforms,
  );
  const selectedProviders = useMemo(
    () => mapPlatformsToProviders(selectedPlatforms),
    [selectedPlatforms],
  );
  const [shellSecondaryQueriesReady, setShellSecondaryQueriesReady] =
    useState(false);
  const [dashboardPrimaryReady, setDashboardPrimaryReady] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const isCollaborator = isWorkspaceMemberTier(user?.tier);
  const [brands, setBrands] = useState(user?.brands || []);
  const [company, setCompany] = useState(user?.company || {});
  const [companies, setCompanies] = useState((user as any)?.companies || []);
  const [currentBrand, setCurrentBrand] = useState(user?.brands[0] || {});
  const clearVisibilitySubscriptionsForBrand = useSocketStore(
    (state) => state.clearVisibilitySubscriptionsForBrand,
  );
  const clearPromptsSubscriptionsForBrand = useSocketStore(
    (state) => state.clearPromptsSubscriptionsForBrand,
  );
  const previousBrandIdRef = useRef<string | null>(null);
  const location = useLocation();
  const isDataChatRoute = location.pathname === "/dashboard/data-chat";
  const isDashboardOverviewRoute = location.pathname === "/dashboard/overview";
  const [predefinedCountries, setPredefinedCountries] = useState<
    { label: string; value: string; icon: JSX.Element }[]
  >([]);
  const showCustomFilters =
    breadcrumb === "Tasks" || breadcrumb === "Foundation";
  const showCountriesSelect = breadcrumb === "Prompts";
  const showTagsSelect = false;
  const showPlatformsSelect =
    breadcrumb !== "Settings" &&
    breadcrumb !== "Plans & Pricing" &&
    breadcrumb !== "Foundation" &&
    breadcrumb !== "Support" &&
    !isDataChatRoute;
  const showTimeRangeSelect =
    breadcrumb !== "Settings" &&
    breadcrumb !== "Cluster Actions" &&
    breadcrumb !== "Plans & Pricing" &&
    breadcrumb !== "Support" &&
    breadcrumb !== "Competitors" &&
    !isDataChatRoute;
  const [activeItem, setActiveItem] = useState<string>("Dashboard");
  const [isTrialEnded, setIsTrialEnded] = useState(false);
  const [planIsEnded, setPlanIsEnded] = useState(false);
  const isSourcesRoute =
    location.pathname === "/dashboard/citations" ||
    location.pathname.startsWith("/dashboard/citations/");
  const isCitedDomainsRoute = location.pathname.endsWith("/cited-domains");
  const showSourceTypeSelect = isSourcesRoute;
  const sourceTypeOptions = isCitedDomainsRoute
    ? DOMAIN_TYPE_OPTIONS
    : URL_TYPE_OPTIONS;
  const sourceTypeValue = isCitedDomainsRoute
    ? citationsDomainTypeFilter
    : citationsUrlTypeFilter;
  const handleSourceTypeChange = (value: string[]) => {
    const normalized = normalizeSourceTypeFilterValues(value);
    if (isCitedDomainsRoute) {
      setCitationsDomainTypeFilter(normalized);
      return;
    }
    setCitationsUrlTypeFilter(normalized);
  };
  const [plan, setPlan] = useState<Plan | string>();
  const { data: billingSubscription } = useQuery({
    queryKey: ["billing-subscription", user?.id ?? "me"],
    queryFn: billingAPI.getSubscription,
    enabled: !isCollaborator && Boolean(user?.id),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const { data: providerSettings } = useQuery({
    queryKey: ["provider-overrides", user?.id ?? "me"],
    queryFn: authAPI.getProviderOverrides,
    enabled:
      Boolean(user?.id) &&
      (isDashboardOverviewRoute
        ? dashboardPrimaryReady
        : shellSecondaryQueriesReady),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const platformOptions = useMemo(() => {
    const providerEffective = providerSettings?.effective;
    const limits =
      typeof plan === "string" || !plan
        ? undefined
        : (plan.limits as Record<string, unknown>);
    const isUserOnGrowthOrHigher = isGrowthPlanOrHigher(user?.tier, plan);

    const toBool = (value: unknown, fallback: boolean) => {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value > 0;
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return fallback;
        if (["true", "1", "yes", "on"].includes(normalized)) return true;
        if (["false", "0", "no", "off"].includes(normalized)) return false;
      }
      return fallback;
    };

    const enabledFromPlan = {
      chatgpt: toBool(limits?.["ai.chatgpt.enabled"], true),
      perplexity: toBool(limits?.["ai.perplexity.enabled"], true),
      overviews: toBool(limits?.["ai.overviews.enabled"], true),
      aiMode: toBool(
        limits?.["ai.mode.enabled"] ?? limits?.["ai.aimode.enabled"],
        isUserOnGrowthOrHigher,
      ),
      gemini: toBool(limits?.["ai.gemini.enabled"], false),
      grok: toBool(limits?.["ai.grok.enabled"], false),
      copilot: toBool(limits?.["ai.copilot.enabled"], false),
    };
    const enabled = providerEffective
      ? {
          chatgpt: Boolean(providerEffective.chatgpt),
          perplexity: Boolean(providerEffective.perplexity),
          overviews: Boolean(providerEffective.overviews),
          aiMode: Boolean(providerEffective.aiMode),
          gemini: Boolean(providerEffective.gemini),
          grok: Boolean(providerEffective.grok),
          copilot: Boolean(providerEffective.copilot),
        }
      : enabledFromPlan;
    return optionsPlatforms.filter((option) => {
      if (option.value === "chatgpt") return enabled.chatgpt;
      if (option.value === "perplexity") return enabled.perplexity;
      if (option.value === "ai-overviews") return enabled.overviews;
      if (option.value === "ai-mode") return enabled.aiMode;
      if (option.value === "gemini") return enabled.gemini;
      if (option.value === "grok") return enabled.grok;
      if (option.value === "copilot") return enabled.copilot;
      return true;
    });
  }, [plan, providerSettings?.effective, user?.tier]);

  useEffect(() => {
    const filtered = selectedPlatforms.filter((platform) =>
      platformOptions.some((option) => option.value === platform),
    );
    if (
      filtered.length === selectedPlatforms.length &&
      filtered.every((value, index) => value === selectedPlatforms[index])
    ) {
      return;
    }
    setSelectedPlatforms(filtered);
  }, [platformOptions, selectedPlatforms, setSelectedPlatforms]);
  const isBlured = [
    "/dashboard/citations",
    "/dashboard/get-cited",
    "/dashboard/overview",
    "/dashboard/data-chat",
    "/dashboard/prompts",
    "/dashboard/get-cited/community-replies",
    "/dashboard/get-cited/contact-publishers",
    "/dashboard/competitors",
    "/dashboard/get-cited/foundation",
    "/dashboard/get-cited/cluster-actions",
    "/dashboard/get-cited/strategy",
  ].includes(location.pathname);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const outletKey = currentBrand?.brandId || currentBrand?.id || "no-brand";
  const dataChatBrandId = currentBrand?.brandId || currentBrand?.id || "";
  const dataChatThreadId = isDataChatRoute
    ? searchParams.get("threadId")
    : null;
  const isDataChatNewChat = isDataChatRoute && !dataChatThreadId;
  const { data: dataChatThreadResponse } = useQuery({
    queryKey: ["data-chat-thread", dataChatBrandId, dataChatThreadId],
    queryFn: async () => {
      if (!dataChatBrandId || !dataChatThreadId) {
        throw new Error("Missing chat thread");
      }
      return dataChatAPI.getThread(dataChatBrandId, dataChatThreadId);
    },
    enabled: Boolean(isDataChatRoute && dataChatBrandId && dataChatThreadId),
    staleTime: 30_000,
  });
  const dataChatTitle =
    dataChatThreadResponse?.thread?.title || t("dataChat.thread.new");
  const selectedCompany = useMemo(() => {
    const currentCompanyId =
      (currentBrand as any)?.companyId ||
      (currentBrand as any)?.company?.id ||
      (currentBrand as any)?.company?.companyId ||
      "";
    if (currentCompanyId) {
      const match = companies.find(
        (item: any) =>
          item?.id === currentCompanyId || item?.companyId === currentCompanyId,
      );
      if (match) return match;
      if ((currentBrand as any)?.company) return (currentBrand as any).company;
    }
    return company || {};
  }, [companies, company, currentBrand]);
  const { data: onboardingVideoConfig } = useQuery<OnboardingVideoConfig>({
    queryKey: ["remoteConfig", "onboardingVideo"],
    queryFn: async () => {
      await fetchAndActivate(remoteConfig);
      const rawValue = getValue(remoteConfig, "onboardingVideo").asString();
      try {
        const parsed = JSON.parse(
          rawValue || "{}",
        ) as Partial<OnboardingVideoConfig>;
        return {
          enabled: Boolean(parsed.enabled),
          url: typeof parsed.url === "string" ? parsed.url : "",
        };
      } catch {
        return { enabled: false, url: "" };
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const persistWorkspaceSelection = (
    userId: string | undefined,
    selectedCompany: any,
    selectedBrand: any,
  ) => {
    if (!userId) return;
    const companyId =
      getWorkspaceId(selectedCompany) || getBrandCompanyId(selectedBrand);
    const brandId = getWorkspaceId(selectedBrand);
    if (companyId) {
      window.localStorage.setItem(
        `nobori_last_company_id:${userId}`,
        companyId,
      );
    }
    if (brandId) {
      window.localStorage.setItem(`nobori_last_brand_id:${userId}`, brandId);
    }
  };

  const selectWorkspace = (
    incomingBrands: any[],
    incomingCompanies: any[],
    fallbackCompany: any,
    userId: string | undefined,
  ) => {
    const selection = resolveWorkspaceSelection({
      brands: incomingBrands || [],
      companies: incomingCompanies || [],
      requestedBrandId: searchParams.get("brandId"),
      savedCompanyId: userId
        ? window.localStorage.getItem(`nobori_last_company_id:${userId}`)
        : null,
      savedBrandId: userId
        ? window.localStorage.getItem(`nobori_last_brand_id:${userId}`)
        : null,
    });
    const nextCompany = selection.company || fallbackCompany || {};
    const nextBrand = selection.brand || {};
    setCompany(nextCompany);
    setCurrentBrand(nextBrand);
    persistWorkspaceSelection(userId, nextCompany, nextBrand);
    if (getWorkspaceId(nextBrand)) {
      const params = new URLSearchParams(searchParams);
      params.set("brandId", getWorkspaceId(nextBrand));
      setSearchParams(params, { replace: true });
    } else {
      const params = new URLSearchParams(searchParams);
      params.delete("brandId");
      setSearchParams(params, { replace: true });
    }
  };

  const handleSetCurrentBrand = (brand: any) => {
    setCurrentBrand(brand);
    const companyId = getBrandCompanyId(brand);
    const nextCompany =
      companies.find((item: any) => getWorkspaceId(item) === companyId) ||
      brand?.company ||
      {};
    setCompany(nextCompany);
    persistWorkspaceSelection(
      currentUser?.id || user?.id,
      nextCompany,
      brand,
    );
    if (brand?.brandId || brand?.id) {
      const params = new URLSearchParams(searchParams);
      params.set("brandId", brand.brandId || brand.id);
      setSearchParams(params, { replace: true });
    }
  };

  const handleSetCurrentCompany = (nextCompany: any) => {
    const companyId = getWorkspaceId(nextCompany);
    const userId = currentUser?.id || user?.id;
    const savedBrandId = userId
      ? window.localStorage.getItem(`nobori_last_brand_id:${userId}`)
      : null;
    const nextBrand =
      brands.find(
        (brand: any) =>
          getWorkspaceId(brand) === savedBrandId &&
          getBrandCompanyId(brand) === companyId,
      ) ||
      brands.find((brand: any) => getBrandCompanyId(brand) === companyId) ||
      null;
    setCompany(nextCompany);
    persistWorkspaceSelection(userId, nextCompany, nextBrand);
    if (nextBrand) {
      handleSetCurrentBrand(nextBrand);
      return;
    }
    setCurrentBrand({});
    const params = new URLSearchParams(searchParams);
    params.delete("brandId");
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    setShellSecondaryQueriesReady(false);
    const timeout = window.setTimeout(
      () => setShellSecondaryQueriesReady(true),
      SHELL_SECONDARY_QUERY_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [location.pathname, currentBrand?.brandId, currentBrand?.id]);

  useEffect(() => {
    setDashboardPrimaryReady(false);
  }, [
    location.pathname,
    currentBrand?.brandId,
    currentBrand?.id,
    timeRange,
    dateRange,
    selectedProviders,
  ]);

  useEffect(() => {
    if ((window as any).$crisp) {
      if (user) {
        (window as any).$crisp.push(["set", "user:email", [user.email]]);
        (window as any).$crisp.push(["do", "chat:close"]);
      } else {
        (window as any).$crisp.push(["do", "session:reset"]);
        (window as any).$crisp.push(["do", "chat:hide"]);
      }
    }
  }, [user]);

  useEffect(() => {
    const currentId = currentBrand?.brandId || currentBrand?.id || null;
    const previousId = previousBrandIdRef.current;
    const brandChanged = previousId !== currentId;
    if (previousId && brandChanged) {
      clearVisibilitySubscriptionsForBrand(previousId);
      clearPromptsSubscriptionsForBrand(previousId);
    }
    // Provider filters are persisted globally; reset on brand change so a new
    // brand starts with all entitled providers instead of stale prior filters.
    if (brandChanged && selectedPlatforms.length) {
      setSelectedPlatforms([]);
    }
    if (brandChanged && currentId) {
      const nextRange = currentBrand?.createdDuringSession ? "1d" : "7d";
      setTimeRange(nextRange);
      setDateRange(getDateRangeForPreset(nextRange));
    }
    previousBrandIdRef.current = currentId;
  }, [
    currentBrand,
    clearVisibilitySubscriptionsForBrand,
    clearPromptsSubscriptionsForBrand,
    selectedPlatforms.length,
    setSelectedPlatforms,
  ]);

  useEffect(() => {
    if ((window as any).$crisp) {
      (window as any).$crisp.push(["do", "chat:close"]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isSourcesRoute) return;
    setCitationsUrlTypeFilter([ALL_TYPES_FILTER_VALUE]);
    setCitationsDomainTypeFilter([ALL_TYPES_FILTER_VALUE]);
  }, [isSourcesRoute]);

  const navigate = useNavigate();
  const handleAgentNewChat = () => {
    const nextParams = new URLSearchParams(searchParams);
    if (dataChatBrandId) {
      nextParams.set("brandId", dataChatBrandId);
    }
    nextParams.delete("threadId");
    setSearchParams(nextParams, { replace: true });
    capturePosthogEvent("agent_new_chat_started", {
      brand_id: dataChatBrandId || null,
    });
  };
  const [loadedImage, setLoadedImage] = useState(false);
  const imageConfig: Record<string, any> = {
    "/dashboard/overview": dashboardBlur,
    "/dashboard/prompts": promptsBlur,
    "/dashboard/citations": citationBlur,
    "/dashboard/competitors": competitorsBlur,
    "/dashboard/get-cited/foundation": foundationBlur,
    "/dashboard/get-cited/cluster-actions": foundationBlur,
    "/dashboard/get-cited/strategy": strategyBlur,
  };
  useEffect(() => {
    const navItems = data.navMain.flatMap((nav) =>
      (nav.items || []).flatMap((item) =>
        item.innerItems ? [...item.innerItems, item] : [item],
      ),
    );
    const foundItem = navItems.find((item) => {
      const fullPath = `/dashboard/${item.url}`;
      return (
        location.pathname === fullPath ||
        location.pathname.startsWith(`${fullPath}/`)
      );
    });
    if (foundItem) {
      setActiveItem(foundItem?.title || "");
      setBreadcrumb(foundItem?.title || "");
    }

    const img = new Image();
    img.src = imageConfig[location.pathname as string];
    img.onload = () => {
      setLoadedImage(true);
    };
  }, [location.pathname]);

  useEffect(() => {
    const status = String(billingSubscription?.subscription_status || "")
      .trim()
      .toLowerCase();
    const trialTierEnded =
      user?.tier === "trial" && hasDateEnded(user?.expired_at);
    const stripeTrialEnded =
      status === "trialing" && hasDateEnded(billingSubscription?.trial_end);
    setIsTrialEnded(trialTierEnded || stripeTrialEnded);

    if (!status) {
      setPlanIsEnded(false);
      return;
    }
    setPlanIsEnded(BILLING_PAYMENT_ISSUE_STATUSES.has(status));
  }, [
    billingSubscription?.subscription_status,
    billingSubscription?.trial_end,
    user?.expired_at,
    user?.tier,
  ]);

  useEffect(() => {
    if (
      !user ||
      !user.tier ||
      !user.brands.length ||
      !Object.keys(user.company).length
    ) {
      const getMe = async () => {
        try {
          const { brands, user, company, companies } = await authAPI.getMe();
          setCurrentUser(user);
          setBrands(brands);
          setCompanies(companies || []);
          selectWorkspace(
            brands,
            companies || [],
            company,
            user?.id,
          );
        } catch {
          setBrands([]);
        }
      };
      getMe();
    } else {
      setBrands(user.brands || []);
      setCompanies((user as any).companies || []);
      selectWorkspace(
        user.brands || [],
        (user as any).companies || [],
        user.company,
        user.id,
      );
      setCurrentUser(user);
    }
  }, [user]);

  function getCountriesOption(codes: any[]) {
    const countriesOptions = countries
      .filter(({ code }) => codes.includes(code))
      .map(({ code, name, icon }) => ({
        label: name,
        value: code,
        icon,
      }));
    setPredefinedCountries(countriesOptions);
  }

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["getPlans"],
    queryFn: async () => {
      const result = await configAPI.getPlans();
      return result || [];
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    const plan = getUserPlan(plans, user);
    setPlan(isLoading ? "Loading..." : plan);
  }, [plans, isLoading, user]);

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("hasSeenDashboardModal");
    if (hasSeenModal !== "true" && onboardingVideoConfig) {
      setOpen(onboardingVideoConfig.enabled);
      localStorage.setItem("hasSeenDashboardModal", "true");
    }
  }, [onboardingVideoConfig]);

  const onboardingVideoUrl = useMemo(
    () => getEmbeddableVideoUrl(onboardingVideoConfig?.url),
    [onboardingVideoConfig?.url],
  );

  return (
    <SidebarProvider className="h-svh overflow-hidden bg-[#0a0a0a]">
      <OnboardingHighlightManager />
      <AppSidebar
        setBrands={setBrands}
        isTrialEnded={isTrialEnded}
        activeItem={activeItem}
        brands={brands}
        company={selectedCompany}
        companies={companies}
        user={currentUser}
        setCurrentBrand={handleSetCurrentBrand}
        setCurrentCompany={handleSetCurrentCompany}
        currentBrand={currentBrand}
      />
      <SidebarInset className="h-full overflow-hidden">
        {open ? (
          <div className="relative h-full">
            <img
              className="w-full h-full object-fill blur-[2px]"
              src={background}
              alt=""
            />
            <div className="absolute inset-0 bg-black/20 backdrop-blur-[1.5px]" />
            <Dialog open={open} onOpenChange={() => setOpen(false)}>
              <DialogContent
                className={`rounded-xl border border-[#D4D4D4] bg-[#F5F5F5] shadow-[0_8px_32px_rgba(0,0,0,0.22)] ${
                  onboardingVideoUrl
                    ? "w-[calc(100vw-2rem)] max-w-[768px] sm:max-w-[768px] max-h-[calc(100vh-2rem)] p-0 overflow-x-hidden overflow-y-auto md:overflow-hidden"
                    : "w-[calc(100vw-2rem)] max-w-[448px] sm:max-w-[448px] max-h-[calc(100vh-2rem)] overflow-y-auto p-6"
                }`}
                showCloseButton={false}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
              >
                {onboardingVideoUrl ? (
                  <div className="relative grid grid-cols-1 md:grid-cols-2">
                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[calc(100%-4rem)] max-h-[312px] w-px -translate-x-1/2 -translate-y-1/2 bg-[#F4D2BF] md:block" />
                    <div className="flex flex-col items-center justify-center px-6 py-8 text-center sm:px-8 md:px-8 md:py-10">
                      <DialogHeader className="items-center gap-6 text-center sm:text-center">
                        <div className="grid h-[64px] w-[64px] place-items-center rounded-2xl bg-black shadow-[0_8px_18px_rgba(0,0,0,0.18)]">
                          <AnimatedNoboriLogoScanner
                            size={36}
                            intensity={1.15}
                            durationSec={1.15}
                          />
                        </div>
                        <DialogTitle className="mx-auto w-full max-w-[395px] text-center text-lg leading-7 font-semibold text-[#0A0A0A]">
                          <LocalizedText id="legacy.analyzing.your.brand.visibility.1xvrgkm" />
                        </DialogTitle>
                        <DialogDescription className="w-full max-w-[395px] text-center text-sm leading-5 text-[#737373]">
                          <LocalizedText id="legacy.were.scanning.ai.models.to.prepare.your.personalized.wexsx7" />
                        </DialogDescription>
                      </DialogHeader>
                      <Button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="mt-2 h-12 w-[156px] min-w-[156px] cursor-pointer rounded-[10px] border-0 border-none bg-[#FF5E00] px-4 py-3 text-base font-medium text-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] hover:bg-[#ff8138] hover:text-white"
                      >
                        <LocalizedText id="legacy.start.exploring.f15u1l" />
                      </Button>
                      <div className="mt-6 h-8 w-full max-w-[280px] text-center text-xs leading-4 text-[#737373]">
                        <LocalizedText id="legacy.you.can.continue.using.nobori.while.we.finish.pp47sh" />
                      </div>
                    </div>
                    <div className="mx-auto h-px w-[calc(100%-3rem)] max-w-[312px] bg-[#F4D2BF] md:hidden" />
                    <div className="px-6 py-7 sm:px-8 md:px-6 md:py-8">
                      <div className="flex items-start justify-between gap-2">
                        <div className="h-[40px] w-[220px] flex-none order-0 grow text-sm leading-5 font-semibold text-[#737373]">
                          <LocalizedText id="legacy.hang.tight.meanwhile.check.out.our.quick.guide.1n22ng7" />
                        </div>
                        <img src={trialArrow} alt="" className="h-auto w-12" />
                      </div>
                      <div className="mt-6 w-full overflow-hidden rounded-3xl border border-black/10 bg-black shadow-[0_18px_40px_rgba(242,106,44,0.24)] md:max-w-[328px]">
                        <div className="relative w-full aspect-[328/240]">
                          <iframe
                            className="absolute inset-0 h-full w-full"
                            src={onboardingVideoUrl}
                            title={t("legacy.onboarding.video.1jck2zz")}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <DialogHeader className="items-center gap-6 text-center sm:text-center">
                    <div className="grid h-[64px] w-[64px] place-items-center rounded-2xl bg-black shadow-[0_8px_18px_rgba(0,0,0,0.18)]">
                      <AnimatedNoboriLogoScanner
                        size={36}
                        intensity={1.15}
                        durationSec={1.15}
                      />
                    </div>
                    <DialogTitle className="mx-auto w-full max-w-[395px] text-center text-lg leading-7 font-semibold text-[#0A0A0A]">
                      <LocalizedText id="legacy.analyzing.your.brand.visibility.1xvrgkm" />
                    </DialogTitle>
                    <div className="flex h-10 w-full max-w-[395px] items-center justify-center gap-2">
                      <DialogDescription className="w-full text-center text-sm leading-5 text-[#737373]">
                        <LocalizedText id="legacy.were.scanning.ai.models.to.prepare.your.personalized.1omuhx0" />
                      </DialogDescription>
                    </div>
                    <div
                      className="mt-1 flex flex-col items-center gap-5"
                      role="status"
                      aria-label={t(
                        "legacy.generating.prompts.and.clusters.1r8os4u",
                      )}
                    >
                      <Button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="h-12 w-[156px] min-w-[156px] cursor-pointer rounded-[10px] border-0 border-none bg-[#FF5E00] px-4 py-3 text-base font-medium text-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] hover:bg-[#ff8138] hover:text-white"
                      >
                        <LocalizedText id="legacy.start.exploring.f15u1l" />
                      </Button>
                      <div className="h-8 w-full max-w-[280px] text-center text-xs leading-4 text-[#737373]">
                        <LocalizedText id="legacy.you.can.continue.using.nobori.while.we.finish.pp47sh" />
                      </div>
                    </div>
                  </DialogHeader>
                )}
              </DialogContent>
            </Dialog>
          </div>
        ) : !isCollaborator && isBlured && (isTrialEnded || planIsEnded) ? (
          <div
            className={`animate-fade-in bg-no-repeat  bg-cover relative flex justify-center relative h-full transition-opacity duration-1000 ${
              loadedImage ? "opacity-100" : "opacity-0"
            }`}
            style={{
              backgroundImage: `url(${imageConfig[location.pathname]})`,
            }}
          >
            <div className="absolute inset-0 bg-black/30 backdrop-blur-xs" />
            <div className="border w-full max-w-[423px]  shadow-xl p-6 flex flex-col items-center gap-4 rounded-md bg-[#f5f5f5] z-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {planIsEnded ? (
                <CircleAlert
                  strokeWidth={1.5}
                  size="34"
                  className="text-[#DC2626]"
                />
              ) : (
                <CalendarClock
                  strokeWidth={1.5}
                  size="34"
                  className="text-[#FF5E00]"
                />
              )}
              <h1 className="text-center text-foreground text-lg font-semibold">
                {planIsEnded
                  ? "We couldn’t charge you"
                  : "Your 7-day trial has ended"}
              </h1>
              <div className="text-center text-muted-foreground text-sm">
                {planIsEnded
                  ? "Update your payment method to keep your access"
                  : "Your data, prompts, and insights are safely stored. Upgrade to keep tracking your brand's visibility."}
              </div>
              <div className="border rounded-md w-full flex flex-col">
                <div className="px-6 pt-6">
                  <div className="flex justify-between w-full">
                    <div className="flex text-sm items-center gap-3">
                      <Bot></Bot>{" "}
                      <LocalizedText id="legacy.access.to.all.ai.platforms.5aykjj" />
                    </div>
                    <div>
                      <BadgeCheck className="text-[#16A34A]" />
                    </div>
                  </div>
                  <Separator className="my-3"></Separator>

                  <div className="flex justify-between w-full">
                    <div className="flex text-sm items-center gap-3">
                      <ScanEye></ScanEye>
                      <LocalizedText id="legacy.full.visibility.analytics.meq5za" />
                    </div>
                    <div>
                      <BadgeCheck className="text-[#16A34A]" />
                    </div>
                  </div>
                  <Separator className="my-3"></Separator>

                  <div className="flex justify-between w-full">
                    <div className="flex text-sm items-center gap-3">
                      <Zap />
                      <LocalizedText id="legacy.daily.updates.and.smart.actions.17g6yss" />{" "}
                    </div>
                    <div>
                      <BadgeCheck className="text-[#16A34A]" />
                    </div>
                  </div>
                </div>
                <Separator className="mt-3"></Separator>
                <div className="text-center text-muted-foreground text-xs p-1">
                  <LocalizedText id="legacy.and.more.than.100.features.1vqtegg" />
                </div>
              </div>
              <Button
                className="w-full hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                onClick={() => {
                  navigate(`/dashboard/plans-pricing`);
                  setActiveItem("Plans & Pricing");
                }}
              >
                {planIsEnded ? "Update Payment" : "Upgrade & Continue"}
              </Button>
              <div className="text-xs text-muted-foreground font-normal">
                <LocalizedText id="legacy.need.help.or.a.custom.plan.16lhm8t" />{" "}
                <span
                  onClick={() => {
                    navigate("/dashboard/support");
                  }}
                  className=" cursor-pointer underline underline-offset-2 text-secondary-foreground font-medium"
                >
                  <LocalizedText id="legacy.contact.us.14c3h4z" />
                </span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <header className="sticky top-0 z-20 flex h-16 min-w-0 shrink-0 items-center gap-2 border-b border-[#F1F1F1] bg-background px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              {isDataChatRoute ? (
                <>
                  <div className="flex min-w-0 flex-1 items-center">
                    <div
                      className="min-w-0 px-2 py-1 text-left text-sm font-semibold text-[#525252]"
                      title={dataChatTitle}
                    >
                      <span className="truncate">{dataChatTitle}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {isDataChatNewChat ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setOpen(true)}
                        className="h-9 rounded-[8px] border-[#E5E5E5] bg-white px-3 text-sm font-medium text-[#111111] shadow-none hover:bg-[#FAFAFA] hover:text-[#111111]"
                      >
                        <img
                          src={graduationCapIcon}
                          alt=""
                          aria-hidden="true"
                          className="h-4 w-4"
                        />
                        {t("dataChat.shell.learn")}
                      </Button>
                    ) : (
                      <>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled
                                  aria-label={t("dataChat.shareSoon")}
                                  className="h-9 cursor-not-allowed rounded-[8px] border-[#E5E5E5] bg-white px-3 text-sm font-medium text-[#111111] opacity-70 shadow-none hover:bg-[#F5F5F5] hover:text-[#111111] disabled:opacity-70"
                                >
                                  <Share2 className="h-4 w-4" />
                                  {t("dataChat.action.share")}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {t("dataChat.soon")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          type="button"
                          onClick={handleAgentNewChat}
                          className="h-9 rounded-[8px] bg-[#FF5E00] px-3 text-sm font-medium text-white shadow-none hover:bg-[#EA580C] hover:text-white"
                        >
                          <Plus className="h-4 w-4" />
                          {t("dataChat.thread.new")}
                        </Button>
                      </>
                    )}
                  </div>
                  <Separator
                    orientation="vertical"
                    className="mx-2 data-[orientation=vertical]:h-4"
                  />
                </>
              ) : (
                <>
                  <Breadcrumb className="w-full min-w-0">
                    <BreadcrumbList className="my-4 w-full min-w-0 justify-between">
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink
                          className="text-[#0A0A0A] text-lg font-semibold"
                          href="#"
                        >
                          {t(breadcrumbLabelKeys[breadcrumb] || breadcrumb)}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {showTagsSelect && (
                          <MultiSelect
                            options={[]}
                            selected={selectedTags}
                            disabled={true}
                            onChange={setSelectedTags}
                            badge={
                              <Badge variant="outline">
                                {t("common.soon")}
                              </Badge>
                            }
                            placeholder={t("common.allTags")}
                          >
                            <Tags></Tags>
                          </MultiSelect>
                        )}
                        {showCustomFilters && (
                          <ClusterFilters
                            clusterFilters={clusterFilters}
                            setClusterFilters={setClusterFilters}
                          ></ClusterFilters>
                        )}
                        {showCountriesSelect && (
                          <MultiSelect
                            disabled={
                              location.pathname !== "/dashboard/prompts"
                            }
                            badge={
                              location.pathname !== "/dashboard/prompts" && (
                                <Badge variant="outline">
                                  {t("common.soon")}
                                </Badge>
                              )
                            }
                            options={predefinedCountries}
                            selected={selectedCountries}
                            onChange={setSelectedCountries}
                            placeholder={t("common.allCountries")}
                          >
                            <MapPin />
                          </MultiSelect>
                        )}

                        {showSourceTypeSelect && (
                          <SourceTypeMultiSelect
                            options={sourceTypeOptions}
                            selected={sourceTypeValue}
                            onChange={handleSourceTypeChange}
                            className="max-w-[170px]"
                            renderOptionLabel={(option) =>
                              option.value === ALL_TYPES_FILTER_VALUE ? (
                                option.label
                              ) : isCitedDomainsRoute ? (
                                <span
                                  className={getDomainTypeBadgeClassName(
                                    option.value,
                                  )}
                                >
                                  {option.label}
                                </span>
                              ) : (
                                <span
                                  className={getUrlTypeBadgeClassName(
                                    option.value,
                                  )}
                                >
                                  {option.label}
                                </span>
                              )
                            }
                          >
                            <FileText className="h-4 w-4 text-black" />
                          </SourceTypeMultiSelect>
                        )}
                        {showPlatformsSelect && (
                          <MultiSelect
                            options={platformOptions}
                            selected={selectedPlatforms}
                            className="min-w-[175px]"
                            showSearch={false}
                            isAllSelected={
                              <div className="flex items-center gap-2">
                                <Bot className="text-black"></Bot>
                                <LocalizedText id="common.allPlatforms" />
                              </div>
                            }
                            onChange={setSelectedPlatforms}
                            placeholder={t("common.allPlatforms")}
                          >
                            {" "}
                            <Bot />
                          </MultiSelect>
                        )}
                        {showTimeRangeSelect && (
                          <DateRangeFilter
                            onTimeRangeChange={handleSetTimeRange}
                            value={dateRange}
                            onValueChange={setDateRange}
                          />
                        )}
                      </div>
                    </BreadcrumbList>
                  </Breadcrumb>
                  <Separator
                    orientation="vertical"
                    className="mx-2 data-[orientation=vertical]:h-4"
                  />
                </>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-[8px] border-[#E5E5E5] bg-white p-0 shadow-none hover:bg-[#FAFAFA]"
                  >
                    <Bell></Bell>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <h4 className="leading-none font-medium">
                        <LocalizedText id="legacy.notifications.ahx7yz" />
                      </h4>
                      <Badge variant="secondary">
                        <LocalizedText id="legacy.coming.soon.1eqsri1" />
                      </Badge>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </header>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-4">
              <Outlet
                key={outletKey}
                context={{
                  isTrialEnded,
                  timeRange,
                  setTimeRange: handleSetTimeRange,
                  dateRange,
                  setDateRange,
                  currentBrand,
                  setCurrentBrand: handleSetCurrentBrand,
                  selectedCountries,
                  selectedPlatforms,
                  selectedProviders,
                  setDashboardPrimaryReady,
                  getCountriesOption,
                  predefinedCountries,
                  plan,
                  brands,
                  company: selectedCompany,
                  companies,
                  clusterFilters,
                  citationsUrlTypeFilter,
                  setCitationsUrlTypeFilter,
                  citationsDomainTypeFilter,
                  setCitationsDomainTypeFilter,
                }}
              />
            </div>
          </>
        )}
      </SidebarInset>
    </SidebarProvider>
  );
}
